import { Slider, Select } from '@ets/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from '_common/hooks';

type ZoomControlsProps = {
  zoom: number;
  changeZoom: (newZoom: number) => void;
  zoomValues?: number[];
};

const ZoomControls = ({
  zoom,
  changeZoom,
  zoomValues = [0.1, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 4, 8, 16, 24, 32, 64],
}: ZoomControlsProps) => {
  const zoomSelectValue = useMemo(() => {
    return { label: `${(zoom * 100).toFixed(0)}%`, value: `${zoom}` };
  }, [zoom]);

  const [localZoomValue, setLocalZoomValue] = useState(zoom);
  const [newZoom, setNewZoom] = useState('');
  const debouncedZoom = useDebounce(localZoomValue, 500);
  const zoomOptions = useMemo(
    () => zoomValues.map((zoomValue) => ({ label: `${zoomValue * 100}%`, value: `${zoomValue}` })),
    [zoomValues],
  );

  useEffect(() => {
    setLocalZoomValue(zoom);
  }, [zoom]);

  useEffect(() => {
    changeZoom(debouncedZoom);
  }, [debouncedZoom]);

  return (
    <>
      <div style={{ width: '30.625rem' }}>
        <Slider
          min={+zoomOptions[0].value * 100}
          middle={100}
          max={+zoomOptions[zoomOptions.length - 1].value * 100}
          step={5.5555}
          value={[Number((zoom * 100).toFixed(0))]}
          onValueChange={([value]) => {
            setLocalZoomValue(value / 100);
            changeZoom(value / 100);
          }}
          testId="footer-zoom"
        />
      </div>
      <div style={{ marginLeft: '2rem' }}>
        <Select
          variant="ghost"
          isClearable={false}
          isCreatable
          width="10rem"
          menuWidth="18rem"
          maxMenuHeight={256}
          menuAlignment="end"
          size="m"
          options={zoomOptions}
          onChange={(option: { value: string; label: string } | null, meta) => {
            const value = option?.value;
            if (meta?.option && meta?.option?.value) {
              const zoom = +(meta?.option?.value ?? 0) / 100;
              changeZoom(+zoom);
            } else {
              changeZoom(+(value ?? 0));
            }
          }}
          value={
            zoomOptions.find((defualtValue) => defualtValue.value === zoomSelectValue.value) ||
            zoomSelectValue
          }
          menuPlacement="top"
          menuPosition="fixed"
          onInputChange={(e: string) => {
            setNewZoom(e);
          }}
          inputValue={newZoom}
          formatCreateLabel={(label) => (label.endsWith('%') ? label : `${label}%`)}
          isValidNewOption={(option) =>
            !!option && zoomOptions.every((zoomOption) => `${+zoomOption.value * 100}` !== option)
          }
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 1052,
            }),
          }}
          testId="zoom"
        />
      </div>
    </>
  );
};

export default ZoomControls;
